/**
 * Status indicator component showing connection and conversation state
 */

import React from 'react';
import { AgeProfile } from '../config/ageProfiles.config';
import { VoiceLiveConnectionState, VoiceLiveConversationState } from '../types/voiceLive.types';
import { ListeningIcon, RefreshIcon, SpeakingIcon, AlertIcon, CheckCircleIcon, ReconnectIcon, CircleIcon } from './SvgConsolidated/SvgIcons';

interface StatusIndicatorProps {
  connectionState: VoiceLiveConnectionState;
  conversationState: VoiceLiveConversationState;
  ageProfile: AgeProfile;
}

export default function StatusIndicator({ connectionState, conversationState, ageProfile }: StatusIndicatorProps) {
  const getStatusColor = () => {
    if (connectionState === 'connected') {
      switch (conversationState) {
        case 'listening':
          return 'text-green-500';
        case 'processing':
          return 'text-yellow-500';
        case 'speaking':
          return 'text-blue-500';
        case 'error':
          return 'text-red-500';
        case 'idle':
        default:
          return 'text-green-400'; // Ready state should be green, not grey
      }
    } else {
      switch (connectionState) {
        case 'connecting':
        case 'reconnecting':
          return 'text-yellow-500';
        case 'error':
          return 'text-red-500';
        default:
          return 'text-gray-500';
      }
    }
  };

  const getStatusIcon = () => {
    if (connectionState === 'connected') {
      switch (conversationState) {
        case 'listening':
          return (
            <div className="animate-pulse">
              <ListeningIcon className="w-5 h-5" />
            </div>
          );
        case 'processing':
          return (
            <div className="animate-spin">
              <RefreshIcon className="w-5 h-5" />
            </div>
          );
        case 'speaking':
          return (
            <div className="animate-bounce">
              <SpeakingIcon className="w-5 h-5" />
            </div>
          );
        case 'error':
          return (
            <AlertIcon className="w-5 h-5" />
          );
        case 'idle':
        default:
          return (
            <div className="animate-pulse">
              <CheckCircleIcon className="w-5 h-5" />
            </div>
          );
      }
    } else {
      switch (connectionState) {
        case 'connecting':
        case 'reconnecting':
          return (
            <div className="animate-spin">
              <ReconnectIcon className="w-5 h-5" />
            </div>
          );
        case 'error':
          return (
            <AlertIcon className="w-5 h-5" />
          );
        default:
          return (
            <CircleIcon className="w-5 h-5" />
          );
      }
    }
  };

  const getStatusText = () => {
    if (connectionState === 'connected') {
      switch (conversationState) {
        case 'listening':
          return ageProfile.content.statusMessages.listening;
        case 'processing':
          return ageProfile.content.statusMessages.processing;
        case 'speaking':
          return 'Speaking...';
        case 'error':
          return ageProfile.content.statusMessages.error;
        case 'idle':
        default:
          return 'Ready to chat!'; // Clear ready message instead of generic "Ready"
      }
    } else {
      switch (connectionState) {
        case 'connecting':
          return ageProfile.content.statusMessages.connecting;
        case 'reconnecting':
          return 'Reconnecting...';
        case 'error':
          return ageProfile.content.statusMessages.error;
        default:
          return 'Disconnected';
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className={getStatusColor()}>
        {getStatusIcon()}
      </div>
      <span className={`${ageProfile.theme.typography.bodySize} text-white/90`}>
        {getStatusText()}
      </span>
    </div>
  );
}